import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed, testPushNotification } from "@/lib/pushManager";

export const PushNotificationsCard = () => {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const pushSupported = "PushManager" in window && "serviceWorker" in navigator;

  useEffect(() => {
    if (pushSupported) {
      isPushSubscribed().then(setPushEnabled);
    }
  }, [pushSupported]);

  const handleToggle = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success("Notifiche push disattivate");
      } else {
        const sub = await subscribeToPush();
        if (sub) {
          setPushEnabled(true);
          toast.success("Notifiche push attivate!");
        } else {
          toast.error("Impossibile attivare le notifiche. Controlla i permessi del browser.");
        }
      }
    } catch {
      toast.error("Errore nell'aggiornamento delle notifiche");
    } finally {
      setPushLoading(false);
    }
  };

  const handleTest = async () => {
    try {
      await testPushNotification();
      toast.success("Notifica di test inviata!");
    } catch {
      toast.error("Errore nell'invio della notifica di test");
    }
  };

  return (
    <Card className="border border-border shadow-sm rounded-sm">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          Notifiche Push
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {pushSupported ? (
          <>
            <p className="text-sm text-muted-foreground">
              Ricevi notifiche per i promemoria anche quando il browser è chiuso.
            </p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{pushEnabled ? "Attive" : "Disattivate"}</p>
                <p className="text-xs text-muted-foreground">
                  {pushEnabled ? "Riceverai le notifiche push" : "Attiva per non perdere i promemoria"}
                </p>
              </div>
              <Button
                variant={pushEnabled ? "outline" : "default"}
                size="sm"
                disabled={pushLoading}
                data-testid="push-toggle-btn"
                onClick={handleToggle}
              >
                {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : pushEnabled ? "Disattiva" : "Attiva"}
              </Button>
            </div>
            {pushEnabled && (
              <Button variant="outline" size="sm" className="w-full" data-testid="push-test-btn" onClick={handleTest}>
                Invia notifica di test
              </Button>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Il tuo browser non supporta le notifiche push. Prova con Chrome o Safari su iOS 16.4+.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
